import { useEffect, useId, useState } from "react";
import { StateMessage } from "@/components/StateMessage";
import { ApiError, demoApi, problemMessage } from "@/api/client";
import type { PayRunPrintResult } from "@/api/types";
import { formatMoney } from "@/lib/format";
import "./PayRunPrint.css";

export interface PayRunPrintProps {
  runId: string;
  lineId: string;
  /** Same `navigate` prop shape every other cross-page link in this app already takes
   *  (H2a/YearEnd/Hours/…) — used here only for "Back to pay runs". */
  onNavigate: (path: string) => void;
}

type Status = "loading" | "ready" | "error";

/**
 * `/payroll/runs/{runId}/print/{lineId}` — ef-047-4, db/migrations/0067_pay_run_line_check_print.sql.
 *
 * OUTSIDE THE APP SHELL, same "no chrome" treatment `/time`/`/entry` get — see App.tsx's
 * own routing note. This page carries the FIRST `@media print` stylesheet in the repo
 * (PayRunPrint.css): what a farmer sees on screen and what a physical check prints as are
 * two different renderings of the same data, and a nav bar has no business on either.
 *
 * VISITING THIS URL IS THE PRINT ACTION. There is no separate "confirm" step once a check
 * number is allocated (0067's own header: numbers are never reissued) — landing here
 * either prints a fresh check for a never-printed line, or opens the render payload for
 * one already printed. See `loadOrPrint` below for exactly how that decision is made.
 */
export function PayRunPrint({ runId, lineId, onNavigate }: PayRunPrintProps) {
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PayRunPrintResult | null>(null);

  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const reasonId = useId();
  const earningsId = useId();

  useEffect(() => {
    // De-duped, not aborted — see `loadOrPrintDeduped`'s own remark for why aborting the
    // fetch alone does not stop a StrictMode remount from firing a genuinely SECOND POST
    // against this mutating, check-number-allocating endpoint. `cancelled` still guards
    // the setState calls below so an instance that unmounted before the shared request
    // settles never touches state after the fact.
    let cancelled = false;

    async function load(): Promise<void> {
      setStatus("loading");
      setError(null);
      try {
        const printed = await loadOrPrintDeduped(runId, lineId);
        if (cancelled) return;
        setResult(printed);
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setError(problemMessage(err, "Could not print this check."));
        setStatus("error");
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [runId, lineId]);

  async function handleVoidConfirm(): Promise<void> {
    setVoidError(null);
    setVoidBusy(true);
    try {
      await demoApi.voidPayCheck(runId, lineId, { voidReason });
      const refreshed = await demoApi.getPayCheckPrint(runId, lineId);
      setResult(refreshed);
      setVoiding(false);
      setVoidReason("");
    } catch (err) {
      setVoidError(problemMessage(err, "Could not void this check."));
    } finally {
      setVoidBusy(false);
    }
  }

  return (
    <main className="pay-run-print" data-state={status}>
      {status === "loading" && <StateMessage tone="loading" title="Printing check…" />}

      {status === "error" && (
        <StateMessage
          tone="error"
          title="Could not print this check."
          detail={error ?? undefined}
          action={
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => onNavigate("/payroll/runs")}
            >
              Back to pay runs
            </button>
          }
        />
      )}

      {status === "ready" && result && (
        <>
          <div className="pay-run-print__toolbar">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => onNavigate("/payroll/runs")}
            >
              Back to pay runs
            </button>
            <button type="button" className="btn btn--primary" onClick={() => window.print()}>
              Print
            </button>
          </div>

          <section
            className="pay-run-print__check"
            aria-label={`Check ${result.checkNumber}`}
            data-state={result.voided ? "voided" : "ready"}
          >
            <header className="pay-run-print__check-header">
              <h1>Check #{result.checkNumber}</h1>
              <span className="pay-run-print__date">
                {formatWindow(result.periodStart, result.periodEnd)}
              </span>
            </header>

            {result.voided && (
              <p className="pay-run-print__voided-badge" role="status">
                Voided{result.voidReason ? ` — ${result.voidReason}` : ""}
              </p>
            )}

            <dl className="pay-run-print__fields">
              <div className="pay-run-print__field">
                <dt>Pay to the order of</dt>
                <dd>{result.payeeName}</dd>
              </div>
              <div className="pay-run-print__field">
                <dt>Amount</dt>
                <dd className="pay-run-print__amount">{formatMoney(result.amount)}</dd>
              </div>
            </dl>

            <p className="pay-run-print__words">{result.amountInWords}</p>

            {/* ef-060-8 @ui-check: the Earnings block exists ONLY on a check whose gross includes piece
                pay — an hourly-only check keeps exactly the DOM it always had. The check's amount stays
                the NET; this block explains the gross it came from (gross = hourly + piece). */}
            {result.piecePay > 0 && (
              <section className="pay-run-print__earnings" aria-labelledby={earningsId}>
                <h2 id={earningsId} className="pay-run-print__earnings-title">
                  Earnings
                </h2>
                <dl className="pay-run-print__earnings-list">
                  <div className="pay-run-print__earnings-row">
                    <dt>Hourly pay</dt>
                    <dd className="pay-run-print__earnings-amount">{formatMoney(result.hourlyPay)}</dd>
                  </div>
                  <div className="pay-run-print__earnings-row">
                    <dt>Piece pay</dt>
                    <dd className="pay-run-print__earnings-amount">{formatMoney(result.piecePay)}</dd>
                  </div>
                  <div className="pay-run-print__earnings-row pay-run-print__earnings-row--total">
                    <dt>Gross pay</dt>
                    <dd className="pay-run-print__earnings-amount">{formatMoney(result.gross)}</dd>
                  </div>
                </dl>
              </section>
            )}
          </section>

          {!result.voided && (
            <section className="pay-run-print__void">
              {voidError && (
                <p className="pay-run-print__error" role="alert">
                  {voidError}
                </p>
              )}
              {!voiding ? (
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => setVoiding(true)}
                >
                  Void this check
                </button>
              ) : (
                <div className="pay-run-print__void-form">
                  <label htmlFor={reasonId}>Reason</label>
                  <input
                    id={reasonId}
                    type="text"
                    value={voidReason}
                    onChange={(e) => setVoidReason(e.target.value)}
                    disabled={voidBusy}
                  />
                  <div className="pay-run-print__void-actions">
                    <button
                      type="button"
                      className="btn btn--ghost"
                      disabled={voidBusy}
                      onClick={() => {
                        setVoiding(false);
                        setVoidReason("");
                        setVoidError(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={voidBusy}
                      onClick={() => void handleVoidConfirm()}
                    >
                      {voidBusy ? "Voiding…" : "Void"}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </>
      )}
    </main>
  );
}

/**
 * Landing on this route either PRINTS a fresh check (never-printed line) or DISPLAYS an
 * already-printed one — decided here, once, rather than the caller having to know which
 * state the line was in before it navigated here.
 *
 * POST first, always. A never-printed line's POST succeeds outright (201) and that IS the
 * render payload — no second round trip. An already-printed, not-voided line's POST 409s
 * (`PayRunPrintEndpoints.cs`'s own "already printed" conflict); GET is then the honest
 * follow-up, since something to render already exists. If GET *also* fails (e.g. the run
 * was never finalized — GET's own "not printed yet" 409 would otherwise stand in for a
 * less accurate message), the ORIGINAL POST error is what gets shown — it is the more
 * honest of the two for that case.
 */
async function loadOrPrint(runId: string, lineId: string): Promise<PayRunPrintResult> {
  try {
    return await demoApi.printPayCheck(runId, lineId);
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      try {
        return await demoApi.getPayCheckPrint(runId, lineId);
      } catch {
        throw err;
      }
    }
    throw err;
  }
}

/**
 * De-dupes concurrent `loadOrPrint` calls for the SAME line, never caches a resolved
 * result. `AbortController` alone (this effect's earlier fix, see its own remark) does
 * NOT make the underlying POST safe to fire twice: React 18 StrictMode's
 * mount→cleanup→remount cycle issues the print POST, then aborts it client-side, then
 * issues a SECOND, independent POST from the remount — but on a fast local round trip
 * the FIRST request can already have reached and been committed by the server before the
 * abort signal lands. The result, measured: the aborted instance's request WINS the
 * server-side write (allocates the real check number), the surviving instance's own POST
 * then genuinely 409s against it, and whichever response `page.waitForResponse` happens
 * to observe first is the 409 — even though the page itself recovers via `loadOrPrint`'s
 * own GET fallback and renders the correct check. Sharing the in-flight PROMISE, keyed
 * by `runId:lineId`, means both StrictMode instances await the exact same POST instead
 * of racing two independent ones — this print endpoint allocates a real, sequential
 * check number (0067's own "numbers are never reissued"), so a duplicate write is not
 * merely wasteful the way a duplicate GET is, it burns a check number for nothing.
 */
const loadOrPrintPromises = new Map<string, Promise<PayRunPrintResult>>();

function loadOrPrintDeduped(runId: string, lineId: string): Promise<PayRunPrintResult> {
  const key = `${runId}:${lineId}`;
  let inFlight = loadOrPrintPromises.get(key);
  if (!inFlight) {
    inFlight = loadOrPrint(runId, lineId).finally(() => {
      loadOrPrintPromises.delete(key);
    });
    loadOrPrintPromises.set(key, inFlight);
  }
  return inFlight;
}

function formatWindow(start: string, end: string): string {
  const asDate = (iso: string) => new Date(`${iso}T12:00:00Z`);
  const fmt = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${fmt.format(asDate(start))} – ${fmt.format(asDate(end))}`;
}
